import { EMPTY_OBJECT } from "@/constants/stable-empty";
import {
  getTeam,
  getTeamPermissions,
} from "@/gen_src/proto/chalk/server/v1/team-TeamService_connectquery";
import { useGetGraphQuery } from "@/utils/hooks/use-graph";
import { useSafePollingQuery } from "@/utils/hooks/grpc/use-safe-polling-query";
import { useIdle } from "@/utils/hooks/use-idle";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import React, { PropsWithChildren, useEffect, useMemo, useRef } from "react";

const TEAM_REFETCH_INTERVAL = 60_000; // 60 seconds

/**
 * This is designed to be a singleton, only used once in the root of the project in _app.tsx,
 * rendered by the NetworkingWrapper.
 *
 * It re-fetches the team and team-permissions rpcs every 60 seconds, and if the "active deployment"
 * has changed, it also re-fetches the graph rpc. This ensures that the graph is always up-to-date with
 * the current active deployment.
 */
export const TeamRefreshingWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const idle = useIdle();
  const teamQuery = useSafePollingQuery(getTeam, EMPTY_OBJECT, {
    refetchInterval: idle ? false : TEAM_REFETCH_INTERVAL,
  });
  useSafePollingQuery(getTeamPermissions, EMPTY_OBJECT, {
    refetchInterval: idle ? false : TEAM_REFETCH_INTERVAL,
  });

  const activeDeploymentRef = useRef<string | undefined>(undefined);

  const { environmentId, projectId } = useRouteInfo();

  const activeDeploymentId = useMemo(() => {
    const project = teamQuery.data?.team?.projects.find((p) => p.id === projectId);
    const environment = project?.environments.find((e) => e.id === environmentId);
    return environment?.activeDeploymentId ?? undefined;
  }, [environmentId, projectId, teamQuery.data?.team?.projects]);

  // Uses the same lean default as useGetActiveGraph so this refetch-on-deploy targets the
  // exact query key the app reads. See FE-1074.
  const { refetch: fetchProtoGraph } = useGetGraphQuery(undefined, { enabled: false });

  useEffect(() => {
    if (activeDeploymentId) {
      if (
        activeDeploymentRef.current != null &&
        activeDeploymentRef.current !== activeDeploymentId
      ) {
        fetchProtoGraph();
      }
      activeDeploymentRef.current = activeDeploymentId;
    }
  }, [activeDeploymentId, fetchProtoGraph]);

  return children;
};
